import {
	TrackedCard,
	Area as SlotArea,
	KtStagingArea,
	useConfirmMoveMutation,
	useDeleteCardMutation,
	useInitiateMoveMutation,
	useListStagingAreasQuery,
	useListTrackedCardsQuery,
	useMarkPickedUpMutation,
	useUndoMoveMutation,
} from '../../redux/api/kit-staging';
import { showMoveError, showMoveSuccess } from './moveMessages';

function labelFor(ta: SlotArea | 'machine' | undefined): string {
	const s = String(ta || '');
	if (s === 'jsa') return 'JSA';
	if (s === 'msa') return 'MSA';
	if (s === 'cart') return 'Cart';
	if (s === 'dekit') return 'Dekit';
	if (s === 'machine') return 'Machine';
	if (s === 'unassigned') return 'Unassigned';
	return s || 'destination';
}

export interface KitCardActions {
	/** Active staging areas (+ racks/shelves) — feeds the sidebar's shelf pickers. */
	stagingAreas: KtStagingArea[];
	/** When the tracked-card list last resolved, for the sidebar's "Synced Ns ago". */
	lastSyncedAt: number | null;
	onInitiate: (card: TrackedCard, target_area: SlotArea | 'machine', target_shelf?: number) => Promise<void>;
	onConfirm: (card: TrackedCard, target_area?: SlotArea | 'machine', target_shelf?: number) => Promise<void>;
	onUndo: (card: TrackedCard) => Promise<void>;
	onDelete: (card: TrackedCard) => Promise<void>;
	onMarkPickedUp: (card: TrackedCard) => Promise<void>;
}

/** The tracked-card move/confirm/undo/delete/mark-picked-up mutations, plus the
 *  staging-area data and last-sync timestamp the left `TabletSidebar` needs.
 *  Owned once at the tablet-page level so every board shares one selection +
 *  one sidebar (the KT tablet layout) instead of each board opening its own
 *  detail drawer.
 *
 *  `onAfterDelete` clears the page's selected card once a delete succeeds (the
 *  card no longer exists, so the sidebar should drop back to its idle state).
 *
 *  `onCelebrateSetup` is called when a confirm-move to `machine` returns
 *  `setup_pending === true`, triggering the "Take for Setup" celebration. */
export function useKitCardActions(opts: { onAfterDelete?: () => void; onCelebrateSetup?: () => void } = {}): KitCardActions {
	const { data: stagingAreas } = useListStagingAreasQuery(undefined, { pollingInterval: 10000 });
	const { fulfilledTimeStamp } = useListTrackedCardsQuery({}, { pollingInterval: 10000 });
	const [initiateMove] = useInitiateMoveMutation();
	const [confirmMove] = useConfirmMoveMutation();
	const [undoMove] = useUndoMoveMutation();
	const [deleteCard] = useDeleteCardMutation();
	const [markPickedUp] = useMarkPickedUpMutation();

	const onInitiate = async (c: TrackedCard, ta: SlotArea | 'machine', ts?: number) => {
		try {
			await initiateMove({ id: c.id, target_area: ta, target_shelf: ts }).unwrap();
			showMoveSuccess(`Sending ${c.item_type} to ${labelFor(ta)} — tap CONFIRM to finalize.`);
		} catch (e) {
			showMoveError(e, `Couldn't start the move to ${labelFor(ta)}.`);
		}
	};
	const onConfirm = async (c: TrackedCard, ta?: SlotArea | 'machine', ts?: number) => {
		try {
			const updated = await confirmMove({ id: c.id, target_area: ta, target_shelf: ts }).unwrap();
			showMoveSuccess(ta ? `${c.item_type} moved to ${labelFor(ta)}.` : 'Placement confirmed.');
			if (updated?.setup_pending === true) {
				opts.onCelebrateSetup?.();
			}
		} catch (e) {
			showMoveError(e, "Couldn't complete the move.");
		}
	};
	const onUndo = async (c: TrackedCard) => {
		try {
			await undoMove({ id: c.id }).unwrap();
			showMoveSuccess('Pending move undone.');
		} catch (e) {
			showMoveError(e, "Couldn't undo the pending move.");
		}
	};
	const onDelete = async (c: TrackedCard) => {
		try {
			await deleteCard({ id: c.id }).unwrap();
			opts.onAfterDelete?.();
			showMoveSuccess('Card deleted.');
		} catch (e) {
			showMoveError(e, "Couldn't delete the card.");
		}
	};
	const onMarkPickedUp = async (c: TrackedCard) => {
		try {
			const result = await markPickedUp({ id: c.id }).unwrap();
			const n = result.updated;
			showMoveSuccess(n > 0 ? `Marked picked up (${n} item${n !== 1 ? 's' : ''}).` : 'Already picked up.');
		} catch (e) {
			showMoveError(e, "Couldn't mark as picked up.");
		}
	};

	return {
		stagingAreas: stagingAreas ?? [],
		lastSyncedAt: fulfilledTimeStamp ?? null,
		onInitiate,
		onConfirm,
		onUndo,
		onDelete,
		onMarkPickedUp,
	};
}
